import { encodeKey } from "./utils";

const AUTH_STORAGE_KEY = "flaredrive-auth";

export interface SessionAccount {
  username: string;
  roots: string[];
  canWriteAll: boolean;
}

export interface Session {
  authenticated: boolean;
  account: SessionAccount | null;
  puburl: string;
  hasAccounts: boolean;
}

export const anonymousSession: Session = {
  authenticated: false,
  account: null,
  puburl: "",
  hasAccounts: false,
};

function basicAuth(username: string, password: string) {
  return `Basic ${btoa(`${username}:${password}`)}`;
}

export function getAuthHeader() {
  return localStorage.getItem(AUTH_STORAGE_KEY);
}

export function setAuthHeader(authHeader: string) {
  localStorage.setItem(AUTH_STORAGE_KEY, authHeader);
}

export function clearAuthHeader() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function authHeaders(headers?: HeadersInit) {
  const result = new Headers(headers);
  const authHeader = getAuthHeader();
  if (authHeader) result.set("Authorization", authHeader);
  return result;
}

async function responseText(response: Response) {
  return (await response.text()) || response.statusText;
}

export async function fetchSession() {
  const response = await fetch("/api/session", {
    headers: authHeaders(),
  });

  if (response.status === 401) {
    clearAuthHeader();
    return anonymousSession;
  }
  if (!response.ok) throw new Error(await responseText(response));
  return (await response.json()) as Session;
}

export async function login(username: string, password: string) {
  const authHeader = basicAuth(username, password);
  const response = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) throw new Error(await responseText(response));
  setAuthHeader(authHeader);
  return (await response.json()) as Session;
}

export function logout() {
  clearAuthHeader();
  return anonymousSession;
}

export function fileUrl(key: string, puburl?: string) {
  if (puburl) return `${puburl.replace(/\/+$/, "")}/${encodeKey(key)}`;
  return `/api/file/${encodeKey(key)}`;
}

export function canWritePath(session: Session, key: string) {
  const account = session.account;
  if (!account) return false;

  const normalized = key.replace(/^\/+|\/+$/g, "");
  if (account.canWriteAll) {
    return true;
  }

  return account.roots.some((root) => {
    const rootKey = root.replace(/\/$/, "");
    return normalized === rootKey || normalized.startsWith(root);
  });
}
